// Coverage UI S4 — the auto-advancing hero.
//
// Daily-Acca-style hero strip, but it advances ON ITS OWN (no manual
// swipe required) — a self-rotating spotlight over the honest
// `featuredItems` set (coming leagues closest to live, then live).
// Honest by construction: it shows ONLY real featured items (empty
// ⇒ renders nothing, never a fabricated highlight), the readiness
// figure is the real corpus meter, and a dormant slide is never a
// bet CTA. Auto-advance pauses on hover/focus so it never fights the
// reader; prev/next + dots remain for manual control.
//
// The founder-only "→ Activate" deep-link appears solely on a slide
// whose verified corpus genuinely meets the minimum (readiness 100)
// — and only when is_strikza_admin() is true. The destination route
// is itself <AdminOnly>-guarded and the activation RPCs re-check
// admin + the 4-condition gate server-side; this is a shortcut, not
// a bypass.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { BoardItem } from "../coverageOverview.ts";
import { StatusPill, ReadinessMeter } from "./CoveragePrimitives.tsx";

const mono: React.CSSProperties = {
  fontFamily: "var(--sa-font-mono, monospace)",
};

const ROTATE_MS = 6000;

export function CoverageHero({
  items,
  isAdmin = false,
  intervalMs = ROTATE_MS,
}: {
  readonly items: readonly BoardItem[];
  readonly isAdmin?: boolean;
  /** Auto-advance period; tests pass a large value to assert the
   *  static first slide deterministically. */
  readonly intervalMs?: number;
}): JSX.Element | null {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = items.length;
  const safeIdx = n > 0 ? idx % n : 0;

  // Keep index in range if the featured set shrinks.
  useEffect(() => {
    if (idx >= n && n > 0) setIdx(0);
  }, [idx, n]);

  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  useEffect(() => {
    if (n <= 1) return;
    const t = setInterval(() => {
      if (!pausedRef.current) setIdx((i) => (i + 1) % n);
    }, intervalMs);
    return () => clearInterval(t);
  }, [n, intervalMs]);

  // Honest-empty: nothing has accrued ⇒ render nothing at all.
  if (n === 0) return null;

  const it = items[safeIdx]!;
  const go = (next: number): void => setIdx(((next % n) + n) % n);

  return (
    <div
      data-testid="coverage-hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      style={{
        position: "relative",
        border: "1px solid var(--sa-hairline, #2a2a30)",
        borderRadius: 8,
        padding: "22px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        overflow: "hidden",
        background:
          "linear-gradient(135deg, var(--sa-bg-elevated, #131318) 0%, var(--sa-bg-base, #0c0c0f) 100%)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ ...mono, fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--sa-fg-muted, #9a9aa2)" }}>
          Closest to live
        </span>
        <span style={{ ...mono, fontSize: 10, color: "var(--sa-fg-muted, #6a6a72)" }}>
          {safeIdx + 1}/{n}
        </span>
      </div>

      <div
        key={it.key}
        data-testid={`hero-slide-${it.key}`}
        style={{ display: "flex", flexDirection: "column", gap: 10, minHeight: 96 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <StatusPill status={it.status} />
          <span style={{ ...mono, fontSize: 10, color: "var(--sa-fg-muted, #6a6a72)" }}>
            phase {it.phase}
          </span>
        </div>
        <div style={{ fontSize: 24, color: "var(--sa-fg-primary, #e8e8ea)" }}>
          {it.title}
        </div>
        {it.status === "coming" ? (
          <ReadinessMeter
            pct={it.readiness}
            webCorpus={it.webCorpus}
            corpusMin={it.corpusMin}
          />
        ) : (
          <span style={{ ...mono, fontSize: 11, color: "var(--sa-accent-positive, #1fbf75)" }}>
            verified &amp; live
          </span>
        )}
        {isAdmin && it.status === "coming" && it.readiness === 100 ? (
          <Link
            to="/admin/activation"
            data-testid="hero-activate-link"
            style={{
              ...mono,
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--sa-accent-positive, #1fbf75)",
              textDecoration: "none",
              alignSelf: "flex-start",
              border: "1px solid var(--sa-accent-positive, #1fbf75)",
              borderRadius: 3,
              padding: "5px 11px",
            }}
          >
            → Activate (founder)
          </Link>
        ) : null}
      </div>

      {n > 1 ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            aria-label="Previous"
            data-testid="hero-prev"
            onClick={() => go(safeIdx - 1)}
            style={navBtn}
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Next"
            data-testid="hero-next"
            onClick={() => go(safeIdx + 1)}
            style={navBtn}
          >
            ›
          </button>
          <div
            data-testid="hero-dots"
            style={{ display: "flex", gap: 6, marginLeft: 4 }}
          >
            {items.map((d, i) => (
              <button
                key={d.key}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => go(i)}
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  padding: 0,
                  cursor: "pointer",
                  border: "none",
                  background:
                    i === safeIdx
                      ? "var(--sa-accent-saffron, #e0a106)"
                      : "var(--sa-hairline, #2a2a30)",
                }}
              />
            ))}
          </div>
          <span
            data-testid="hero-autostate"
            style={{ ...mono, fontSize: 9.5, color: "var(--sa-fg-muted, #6a6a72)", marginLeft: "auto" }}
          >
            {paused ? "paused" : "auto"}
          </span>
        </div>
      ) : null}
    </div>
  );
}

const navBtn: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 3,
  background: "transparent",
  color: "var(--sa-fg-primary, #e8e8ea)",
  border: "1px solid var(--sa-hairline, #2a2a30)",
  fontFamily: "var(--sa-font-mono, monospace)",
  fontSize: 15,
  lineHeight: 1,
  cursor: "pointer",
};
